import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Sidebar as ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import type { NavigationTab } from "../../types";
import { CycleType } from "../../types";
import type { DeadlineType } from "../../types/rbac";
import { authService } from "../../services";
import { useRBAC } from "../../hooks/useRBAC";
import { LoadingSpinner } from "../ui/LoadingSpinner";

// Import SVG assets
import selfAppraisalIcon from "../../assets/self_appraisal.svg";
import nominationReviewIcon from "../../assets/nomination_review.svg";
import nominationApprovalIcon from "../../assets/nomination_approval.svg";
import teamReviewIcon from "../../assets/team_review.svg";
// import settingsIcon from "../../assets/settings.svg";
import adminPanelIcon from "../../assets/admin_panel_settings.svg";
import logOutIcon from "../../assets/log_out.svg";
import Dialog from '../ui/Dialog';

interface SidebarProps {
  onTabChange: (tab: NavigationTab) => void;
  onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onTabChange, onLogout }) => {
  // RBAC hook for permission checking and appraisal cycle data
  const { hasAccess, isLoading: rbacLoading, getDaysLeft, getDeadlineDate, appraisalCycle } = useRBAC();
  const location = useLocation();
  const isApprovalsListPage = location.pathname === "/approvals";

  // Derive cycle phase label from appraisalCycle.cycle_type
  const cyclePhaseLabel = (() => {
    const ct = appraisalCycle?.cycle_type;
    if (ct === CycleType.START) return 'Target Settings';
    if (ct === CycleType.MID) return 'Mid Year Review';
    if (ct === CycleType.END) return '360 Feedback Cycle';
    return null;
  })();
  
  // Example static value, replace with actual logic if needed
   const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
 
  const [isCompactViewport, setIsCompactViewport] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1023px)").matches
      : false,
  );
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1023px)").matches
      : false,
  );

  // Keep the single primary sidebar compact on phone/tablet widths. Users can
  // still expand it manually; crossing the desktop breakpoint resets the mode.
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleBreakpointChange = (event: MediaQueryListEvent) => {
      setIsCompactViewport(event.matches);
      setCollapsed(event.matches);
    };

    mediaQuery.addEventListener("change", handleBreakpointChange);
    return () => mediaQuery.removeEventListener("change", handleBreakpointChange);
  }, []);

  useEffect(() => {
    if (!isCompactViewport || collapsed) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCollapsed(true);
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [collapsed, isCompactViewport]);
  
  // No longer need local state for nomination data - use RBAC context
  // Helper to compute days left from an ISO end-date string (local-day calculation) - DEPRECATED, use RBAC
  // const computeDaysLeftFromEndDate = ...
  
  // No useEffect needed - RBAC context provides all deadline data
  
  // Logout handler: shows confirmation dialog first
  const handleLogout = () => {
    setIsLogoutDialogOpen(true);
  };

  const handleMenuNavigation = (tab: NavigationTab) => {
    onTabChange(tab);
    if (isCompactViewport) setCollapsed(true);
  };

  // Confirms logout: clears login-related localStorage and calls parent onLogout
  const confirmLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout API error:", error);
      // Optionally show error to user
    } finally {
      setIsLogoutDialogOpen(false);
      if (onLogout) {
        onLogout();
      }
    }
  };
  // Define all menu items with their required permissions
  const allMenuItems = [
    {
      id: "self-appraisal" as NavigationTab,
      label: "Self Appraisal",
      path: "/self-appraisal",
      // Show Self Appraisal if user has access to OKR OR Nomination
      hasPermission: () => hasAccess('self_appraisal_okr') || hasAccess('self_appraisal_nomination'),
      icon: (
        <img
          src={selfAppraisalIcon}
          alt="Self Appraisal"
          className="w-8 h-8 sidebar-icon"
        />
      ),
    },   {
      id: "nomination-approval" as NavigationTab,
      label: "Approvals",
      path: "/approvals",
      // Show Approvals if user has the top-level approval access
      hasPermission: () => hasAccess('approval'),
      icon: (
        <img
          src={nominationApprovalIcon}
          alt="Approvals"
          className="w-8 h-8 sidebar-icon"
        />
      ),
    },
    {
      id: "nomination-review" as NavigationTab,
      label: "360 Feedback",
      path: "/360-feedback",
      hasPermission: () => hasAccess('nomination_review'),
      icon: (
        <img
          src={nominationReviewIcon}
          alt="360 Feedback"
          className="w-8 h-8 sidebar-icon"
        />
      ),
    },
 
    {
      id: "team-review" as NavigationTab,
      label: "Team Review",
      path: "/team-review",
      hasPermission: () => hasAccess('team_review'),
      icon: (
        <img
          src={teamReviewIcon}
          alt="Team Review"
          className="w-8 h-8 sidebar-icon"
        />
      ),
    },

    {
      id: "hod-review" as NavigationTab,
      label: "HOD Review",
      path: "/hod-review",
      hasPermission: () => hasAccess('hod_review'),
      icon: (
        <img
          src={teamReviewIcon}
          alt="HOD Review"
          className="w-8 h-8 sidebar-icon"
        />
      ),
    },
  ];

  // Filter menu items based on user permissions
  const menuItems = allMenuItems.filter(item => item.hasPermission());

  // Appraisal Admin moved to bottom actions for easier access (rendered in bottom menu)

  // Active flags for bottom menu items so they display selected state like main menu
  // const isSettingsActive = location.pathname === "/settings";
  const isAdminActive = location.pathname.startsWith("/appraisal-admin");

  // Shared styles for both main and bottom menus to keep UI consistent
  // Show loading state while RBAC permissions are being fetched
  if (rbacLoading) {
    return (
      <ProSidebar
        collapsed={false}
        width="280px"
        backgroundColor="#ffffff"
        rootStyles={{
          border: "none",
          borderRight: "1px solid #e2e8f0",
          height: "100vh",
          boxShadow:
            "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div style={{ 
          display: "flex", 
          flexDirection: "column", 
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          padding: "2rem"
        }}>
          <LoadingSpinner />
          <p style={{ 
            marginTop: "1rem", 
            color: "#5F279E",
            fontSize: "0.9rem",
            fontWeight: 500
          }}>Loading menu...</p>
        </div>
      </ProSidebar>
    );
  }

  const sharedMenuItemStyles = {
    button: ({ active }: { active: boolean }) => ({
      backgroundColor: active ? "#5F279E" : "transparent",
      color: active ? "#ffffff" : "#000000",
      padding: collapsed ? "0.5rem 0.25rem" : "1.25rem 1rem",
      margin: collapsed ? "0.375rem 0.625rem" : "0.375rem 1rem",
      borderRadius: "0.5rem",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      border: "none",
      fontSize: "0.9rem",
      fontWeight: 500,
      letterSpacing: "-0.01em",
      display: "flex",
      alignItems: "center",
      justifyContent: collapsed ? "center" : "flex-start",
      "&:hover": {
        backgroundColor: active ? "#7a32c4" : "#f5f2fc",
        transform: "translateY(-1px)",
        boxShadow: active
          ? "0 4px 12px rgba(139, 78, 212, 0.4)"
          : "0 2px 4px rgba(0, 0, 0, 0.04)",
      },
    }),
    icon: ({ active }: { active: boolean }) => ({
      color: active ? "#ffffff" : "#000000",
      marginRight: collapsed ? "0" : "0.875rem",
      width: "35px",
      minWidth: "35px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }),
    label: {
      display: collapsed ? "none" : "block",
      flexGrow: collapsed ? 0 : 1,
      fontWeight: 500,
      fontSize: "0.9rem",
      lineHeight: "1.25rem",
    },
  };

  return (
    <>
      {isCompactViewport && !collapsed && (
        <button
          type="button"
          className="fixed inset-0 z-0 cursor-default bg-black/30"
          onClick={() => setCollapsed(true)}
          aria-label="Close sidebar"
        />
      )}
      <style>
        {`
          .ps-active .sidebar-icon {
            filter: brightness(0) invert(1);
          }
          .sidebar-icon {
            transition: filter 0.2s ease;
          }
        `}
      </style>
      <ProSidebar
      collapsed={collapsed}
      width="280px"
      collapsedWidth="80px"
      backgroundColor="#ffffff"
      rootStyles={{
        border: "none",
        borderRight: "1px solid #e2e8f0",
        height: "100dvh",
        position: "relative",
        zIndex: 10,
        boxShadow:
          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <div className="p-2 lg:p-4">
            <button
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={{
                background: collapsed ? "#ffffff" : "#ffffff",
                border: "1.5px solid #d9ccf4",
                cursor: "pointer",
                width: collapsed ? "100%" : "auto",
                padding: collapsed ? "0.5rem" : "0.75rem",
                borderRadius: "0.75rem",
                color: collapsed ? "#5F279E" : "#7a32c4",
                boxShadow: collapsed
                  ? "0 2px 8px rgba(139, 78, 212, 0.08)"
                  : "0 1px 2px rgba(0,0,0,0.04)",
                transition: "all 0.18s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                outline: "none",
              }}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow =
                  "0 0 0 2px #a478e1, 0 2px 8px rgba(139, 78, 212, 0.08)";
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = collapsed
                  ? "0 2px 8px rgba(139, 78, 212, 0.08)"
                  : "0 1px 2px rgba(0,0,0,0.04)";
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f2fc";
                e.currentTarget.style.borderColor = "#a478e1";
                e.currentTarget.style.color = "#5F279E";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = collapsed ? "#f5f2fc" : "#fafbfc";
                e.currentTarget.style.borderColor = "#d9ccf4";
                e.currentTarget.style.color = collapsed ? "#5F279E" : "#7a32c4";
              }}
            >
              {collapsed ? (
                // Show hamburger icon when collapsed
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{
                    transition: "transform 0.2s",
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              ) : (
                // Show cross icon when expanded
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{
                    transition: "transform 0.2s",
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 6l12 12M6 18L18 6"
                  />
                </svg>
              )}
            </button>
        </div>
        {/* Main Menu */}
  <Menu
    className="py-2"
    menuItemStyles={sharedMenuItemStyles}
  >
          <div style={{ padding: "1.5rem 0" }}>
            {menuItems.map((item) => {
              // Make the self-appraisal menu item active for any nested route
              // (e.g. /self-appraisal/objectives, /self-appraisal/nominations).
              // Also make nomination-approval active for detail pages (/approvals/:id)
              // Also make nomination-review active for detail pages (/360-feedback/:id/review)
              // Also make team-review active for detail pages (/team-review/:id)
              // Also make hod-review active for detail pages (/hod-review/:id)
              const isActive =
                item.id === "self-appraisal"
                  ? location.pathname.startsWith("/self-appraisal")
                  : item.id === "nomination-approval"
                  ? location.pathname.startsWith("/approvals")
                  : item.id === "nomination-review"
                  ? location.pathname.startsWith("/360-feedback")
                  : item.id === "team-review"
                  ? location.pathname.startsWith("/team-review")
                  : item.id === "hod-review"
                  ? location.pathname.startsWith("/hod-review")
                  : location.pathname === item.path;

              return (
                <MenuItem
                  key={item.id}
                  icon={item.icon}
                  active={isActive}
                  title={collapsed ? item.label : undefined}
                  aria-label={collapsed ? item.label : undefined}
                  component={
                    <Link to={item.path} onClick={() => handleMenuNavigation(item.id)} />
                  }
                >
                  {!collapsed && item.label}
                </MenuItem>
              );
            })}
          </div>
        </Menu>

        {/* Deadline Reminder Card - reserve space even when hidden so layout stays consistent */}
        {(() => {
          // Configure which route prefixes should show the deadline widget.
          const enabledPrefixes = ["/self-appraisal", "/360-feedback", "/approvals", "/team-review", "/hod-review"]; 
          const shouldShowWidget = enabledPrefixes.some((p) => location.pathname.startsWith(p));

          // When the sidebar is collapsed we keep the small spacer as before.
          if (collapsed) return (<div style={{ padding: "0 1.5rem", marginTop: "auto" }}></div>);

          // When expanded, always reserve space for the widget so the layout doesn't shift
          // even if the widget is hidden for the current route. Use a minHeight similar
          // to the widget's visual footprint to keep spacing consistent.
          return (
            <div style={{
              padding: "0 1.5rem",
              marginTop: "auto",
              minHeight: "140px",
              flexShrink: location.pathname === "/approvals" ? 0 : undefined,
            }}>
              {shouldShowWidget ? (
              (() => {
                const path = location.pathname;

                // Determine which deadline to show based on the current route
                let deadlineType: DeadlineType | null = null;
                let submissionLabel = '';

                if (isApprovalsListPage) {
                  deadlineType = 'okr_approval';
                  submissionLabel = 'OKR Approval';
                } else if (path.startsWith("/self-appraisal/nominations")) {
                  deadlineType = 'nomination';
                  submissionLabel = 'Nomination Submission';
                } else if (path.startsWith("/360-feedback")) {
                  deadlineType = 'review';
                  submissionLabel = '360 Feedback Review';
                } else if (path.startsWith("/approvals")) {
                  const urlParams = new URLSearchParams(location.search);
                  const canApproveOKRs = hasAccess('okr_approval');
                  const isStartOrMidCycle = appraisalCycle?.cycle_type === CycleType.START
                    || appraisalCycle?.cycle_type === CycleType.MID;
                  const canApproveNominations = hasAccess('nomination_approval') && !isStartOrMidCycle;
                  const noNomination = urlParams.get('no_nomination') === 'true';
                  const showNominationReminder = !noNomination
                    && canApproveNominations
                    && (urlParams.get('tab') === 'nominations' || !canApproveOKRs);

                  if (showNominationReminder) {
                    deadlineType = 'nomination_approval';
                    submissionLabel = 'Nomination Approval';
                  } else {
                    deadlineType = 'okr_approval';
                    submissionLabel = 'OKR Approval';
                  }
                } else if (path.startsWith("/team-review")) {
                  deadlineType = 'review';
                  submissionLabel = 'Team Review';
                } else if (path.startsWith("/hod-review")) {
                  deadlineType = 'hod_review';
                  submissionLabel = 'HOD Review';
                } else if (path.startsWith("/self-appraisal")) {
                  deadlineType = 'self_appraisal';
                  submissionLabel = 'OKR Submission';
                }

                if (!deadlineType) {
                  return <div style={{ height: '100%' }} />;
                }

                // Get deadline data from RBAC context
                const numericDays = getDaysLeft(deadlineType);
                const endDateStrForRoute = getDeadlineDate(deadlineType);

                const hasNumericDays = typeof numericDays === 'number' && !Number.isNaN(numericDays);
                const hasEndDate = Boolean(endDateStrForRoute);

                if (!hasNumericDays && !hasEndDate && !isApprovalsListPage) {
                  return <div style={{ height: '100%' }} />;
                }

                const formatDateWithOrdinal = (value?: string | null) => {
                  if (!value) return '';
                  const date = new Date(value);
                  if (Number.isNaN(date.getTime())) return '';
                  const day = date.getDate();
                  const suffix = (() => {
                    if (day % 10 === 1 && day % 100 !== 11) return 'st';
                    if (day % 10 === 2 && day % 100 !== 12) return 'nd';
                    if (day % 10 === 3 && day % 100 !== 13) return 'rd';
                    return 'th';
                  })();
                  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
                  const month = months[date.getMonth()] ?? '';
                  return `${month} ${day}${suffix}, ${date.getFullYear()}`;
                };

                const palette = (() => {
                  if (!hasNumericDays) {
                    return {
                      bg: '#f5f2fc',
                      border: '#d9ccf4',
                      title: '#5F279E',
                      body: '#5F279E',
                    };
                  }

                  const days = numericDays as number;

                  if (days <= 0) {
                    return {
                      bg: '#fee2e2',
                      border: '#fca5a5',
                      title: '#991b1b',
                      body: '#991b1b',
                    };
                  }

                  if (days <= 5) {
                    return {
                      bg: '#fffbeb',
                      border: '#fef08a',
                      title: '#92400e',
                      body: '#92400e',
                    };
                  }

                  return {
                    bg: '#ecfdf5',
                    border: '#bbf7d0',
                    title: '#065f46',
                    body: '#065f46',
                  };
                })();

                const titleText = (() => {
                  if (hasNumericDays) {
                    const days = numericDays as number;
                    if (days < 0) return 'Deadline Passed';
                    if (days === 0) return 'Last Day!';
                    return `${days} ${days === 1 ? 'Day' : 'Days'} Left!`;
                  }

                  if (hasEndDate) {
                    return 'Upcoming Deadline';
                  }

                  return 'Deadline';
                })();

                const formattedDate = formatDateWithOrdinal(endDateStrForRoute);
                const bodyText = formattedDate
                  ? `${submissionLabel} ends on: ${formattedDate}`
                  : null;

                if (isApprovalsListPage) {
                  const hasReachedStartDate = (startDate?: string) => {
                    if (!startDate) return true;

                    const startDateValue = new Date(`${startDate}T00:00:00`);
                    if (Number.isNaN(startDateValue.getTime())) return true;

                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return startDateValue <= today;
                  };

                  const allApprovalDeadlines: { type: DeadlineType; label: string; startDate?: string }[] = [
                    {
                      type: 'nomination_approval',
                      label: 'Nomination Approval',
                      startDate: appraisalCycle?.nomination_start_date,
                    },
                    {
                      type: 'okr_approval',
                      label: 'OKR Approval',
                      startDate: appraisalCycle?.self_appraisal_okr_start_date,
                    },
                  ];
                  const approvalDeadlines = allApprovalDeadlines.filter((deadline) =>
                    hasReachedStartDate(deadline.startDate),
                  );

                  const cards = approvalDeadlines.flatMap((deadline) => {
                    const daysLeft = getDaysLeft(deadline.type);
                    const endDate = getDeadlineDate(deadline.type);
                    const hasDaysLeft = typeof daysLeft === 'number' && !Number.isNaN(daysLeft);

                    if (!hasDaysLeft && !endDate) return [];

                    const cardPalette = (() => {
                      if (!hasDaysLeft) {
                        return {
                          bg: '#f5f2fc',
                          border: '#d9ccf4',
                          title: '#5F279E',
                          body: '#5F279E',
                        };
                      }

                      if ((daysLeft as number) <= 0) {
                        return {
                          bg: '#fee2e2',
                          border: '#fca5a5',
                          title: '#991b1b',
                          body: '#991b1b',
                        };
                      }

                      if ((daysLeft as number) <= 5) {
                        return {
                          bg: '#fffbeb',
                          border: '#fef08a',
                          title: '#92400e',
                          body: '#92400e',
                        };
                      }

                      return {
                        bg: '#ecfdf5',
                        border: '#bbf7d0',
                        title: '#065f46',
                        body: '#065f46',
                      };
                    })();

                    const cardTitle = (() => {
                      if (!hasDaysLeft) return 'Upcoming Deadline';
                      if ((daysLeft as number) < 0) return 'Deadline Passed';
                      if (daysLeft === 0) return 'Last Day!';
                      return `${daysLeft} ${daysLeft === 1 ? 'Day' : 'Days'} Left!`;
                    })();

                    return [{
                      ...deadline,
                      palette: cardPalette,
                      title: cardTitle,
                      formattedDate: formatDateWithOrdinal(endDate),
                    }];
                  });

                  if (cards.length === 0) {
                    return <div style={{ height: '100%' }} />;
                  }

                  return (
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem',
                        marginBottom: cards.length === 1 ? '1.5rem' : '0.75rem',
                      }}
                    >
                      {cards.map(({ type, label, palette: cardPalette, title, formattedDate: cardDate }) => (
                        <div
                          key={type}
                          style={{
                            backgroundColor: cardPalette.bg,
                            border: `1px solid ${cardPalette.border}`,
                            borderRadius: '0.5rem',
                            padding: '1.25rem',
                            textAlign: 'center',
                            zIndex: 1000,
                            position: 'relative',
                            boxShadow:
                              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                          }}
                        >
                          {cyclePhaseLabel ? (
                            <div
                              style={{
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                color: cardPalette.title,
                                backgroundColor: cardPalette.border,
                                borderRadius: '1rem',
                                padding: '0.15rem 0.6rem',
                                margin: '0 auto 0.5rem',
                                width: 'fit-content',
                              }}
                            >
                              {cyclePhaseLabel}
                            </div>
                          ) : null}
                          <div
                            style={{
                              color: cardPalette.title,
                              fontSize: '1.125rem',
                              fontWeight: 700,
                              textAlign: 'center',
                              lineHeight: '1.25rem',
                              marginBottom: cardDate ? '0.5rem' : 0,
                            }}
                          >
                            {title}
                          </div>
                          {cardDate ? (
                            <div
                              style={{
                                color: cardPalette.body,
                                fontSize: '0.85rem',
                                lineHeight: '1.2rem',
                                fontWeight: 500,
                                textAlign: 'center',
                              }}
                            >
                              {`${label} Ends on: ${cardDate}`}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  );
                }

                return (
                  <div
                    style={{
                      backgroundColor: palette.bg,
                      border: `1px solid ${palette.border}`,
                      borderRadius: '0.5rem',
                      padding: '1.25rem',
                      marginBottom: '1.5rem',
                      textAlign: 'center',
                      boxShadow:
                        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 1000,
                      position: 'relative',
                    }}
                  >
                    {/* Cycle phase badge */}
                    {cyclePhaseLabel ? (
                      <div
                        style={{
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: palette.title,
                          backgroundColor: `${palette.border}`,
                          borderRadius: '1rem',
                          padding: '0.15rem 0.6rem',
                          marginBottom: '0.5rem',
                          display: 'inline-block',
                        }}
                      >
                        {cyclePhaseLabel}
                      </div>
                    ) : null}
                    <div
                      style={{
                        color: palette.title,
                        fontWeight: 700,
                        fontSize: '1.125rem',
                        marginBottom: bodyText ? '0.5rem' : 0,
                        letterSpacing: '-0.025em',
                      }}
                    >
                      {titleText}
                    </div>
                    {bodyText ? (
                      <div
                        style={{
                          color: palette.body,
                          fontSize: '0.85rem',
                          lineHeight: '1.2rem',
                          fontWeight: 500,
                        }}
                      >
                        {bodyText}
                      </div>
                    ) : null}
                  </div>
                );
              })()
            ) : (
              // Placeholder to preserve spacing when widget is hidden for the route
              <div style={{ height: "100%" }} />
            )}
          </div>
        );
      })()}

  {/* Bottom Actions */}
  <Menu
    className="py-2"
    menuItemStyles={sharedMenuItemStyles}
    rootStyles={location.pathname === "/approvals" ? { flexShrink: 0 } : undefined}
  >
          <div
            style={{
              borderTop: "1px solid #e2e8f0",
              padding: "1.5rem 0 1.75rem",
              backgroundColor: "#ffffff",
              marginTop: "auto",
            }}
          >
                  {/* Appraisal Admin - Only visible if user has admin permission */}
            {hasAccess('admin') && (
              <MenuItem
                active={isAdminActive}
                title={collapsed ? "Appraisal Admin" : undefined}
                aria-label={collapsed ? "Appraisal Admin" : undefined}
                icon={
                  <img
                    src={adminPanelIcon}
                    alt="Appraisal Admin"
                    className="w-8 h-8 sidebar-icon"
                  />
                }
                component={<Link to="/appraisal-admin" onClick={() => handleMenuNavigation("appraisal-admin" as NavigationTab)} />}
              >
                {!collapsed && "Appraisal Admin"}
              </MenuItem>
            )}
            {/* <MenuItem
              active={isSettingsActive}
              icon={
                <img
                  src={settingsIcon}
                  alt="Settings"
                  className="w-8 h-8 sidebar-icon"
                />
              }
              component={<Link to="/settings" onClick={() => onTabChange("settings" as NavigationTab)} />}
            >
              Settings
            </MenuItem> */}

          

            <MenuItem
              title={collapsed ? "Log Out" : undefined}
              aria-label={collapsed ? "Log Out" : undefined}
              icon={
                <img
                  src={logOutIcon}
                  alt="Log Out"
                  className="w-8 h-8 sidebar-icon"
                />
              }
              onClick={handleLogout}
            >
              {!collapsed && "Log Out"}
            </MenuItem>
          </div>
        </Menu>
      </div>
    </ProSidebar>
  
    {/* Logout Confirmation Dialog */}
    <Dialog
      isOpen={isLogoutDialogOpen}
      onClose={() => setIsLogoutDialogOpen(false)}
      title="Logout"
      message={
        <div>
          <p className="text-gray-800 mb-3 font-medium">You will be logged out of the system.</p>
          <p className="text-gray-700 mb-4">Are you sure you want to continue?</p>
        </div>
      }
      type="warning"
      showCancel={false}
      customActions={
        <>
          <button
            onClick={() => setIsLogoutDialogOpen(false)}
            className="text-red-500 underline text-sm px-4 py-2 bg-transparent rounded"
          >
            Cancel
          </button>
          <button
            onClick={confirmLogout}
            className="bg-purple-800 text-white px-4 py-2 rounded-lg"
          >
            Log Out
          </button>
        </>
      }
    />
    </>
  );
};

export default Sidebar;
